/**
 * 编辑→变换 子菜单（DESIGN.md §5.3 补充）。
 *
 * 全部十项都挂 `submenu: '变换'`——菜单模型（menu-model.ts）把同一个
 * submenu 标签的动作折成一个二级菜单父项，渲染成 Photoshop 那种
 * 「编辑→变换→缩放/旋转/…」嵌套弹出菜单，不是平铺在编辑菜单里。
 *
 * 「扭曲」「透视」没有实现：它们是真正的非仿射变形（四角独立映射），
 * Mat3 的 6 自由度装不下，需要专门的四角映射引擎（新的手柄数学 + 新的
 * 烘焙路径，不是仿射矩阵能表达的），如实置灰而不是假装能点。
 *
 * 「缩放/旋转/斜切」是约束模式：进自由变换会话，但手柄只解释成那一种
 * 操作（TransformToolController 的 mode 参数）。「旋转180°/90°/翻转」
 * 是离散操作，不需要拖手柄，直接复用 begin/commit 链路瞬间完成。
 */
import type { ActionRegistry } from './registry';

export type TransformConstraintMode = 'free' | 'scale' | 'rotate' | 'skew';

export interface TransformActionDeps {
  /** 开始一个约束模式的自由变换会话。没有可变换图层返回 false。 */
  readonly beginConstrained: (mode: TransformConstraintMode) => boolean;
  readonly rotate90: (direction: 'cw' | 'ccw') => Promise<boolean>;
  readonly rotate180: () => Promise<boolean>;
  readonly flip: (axis: 'horizontal' | 'vertical') => Promise<boolean>;
}

const NO_LAYER_HINT = '没有可变换的像素图层';
const SUBMENU = '变换';

export function registerTransformActions(registry: ActionRegistry, deps: TransformActionDeps): void {
  const registerConstrained = (
    id: string,
    title: string,
    order: number,
    mode: TransformConstraintMode,
  ): void => {
    registry.register({
      id,
      title,
      group: '编辑',
      section: 1,
      order,
      submenu: SUBMENU,
      enabled: (s) => s.hasDocument,
      run: (services) => {
        if (!deps.beginConstrained(mode)) services.hint(NO_LAYER_HINT);
      },
    });
  };

  registerConstrained('edit.transform.scale', '缩放', 1, 'scale');
  registerConstrained('edit.transform.rotate', '旋转', 2, 'rotate');
  registerConstrained('edit.transform.skew', '斜切', 3, 'skew');

  // 如实置灰：见文件头。不是「未接线」，是这次范围里明确不做。
  registry.register({
    id: 'edit.transform.distort',
    title: '扭曲（暂不支持）',
    group: '编辑',
    section: 1,
    order: 4,
    submenu: SUBMENU,
    enabled: () => false,
    run: () => {
      /* 需要非仿射的四角映射引擎，这次不做 */
    },
  });
  registry.register({
    id: 'edit.transform.perspective',
    title: '透视（暂不支持）',
    group: '编辑',
    section: 1,
    order: 5,
    submenu: SUBMENU,
    enabled: () => false,
    run: () => {
      /* 需要非仿射的四角映射引擎，这次不做 */
    },
  });

  const registerQuick = (
    id: string,
    title: string,
    order: number,
    action: () => Promise<boolean>,
  ): void => {
    registry.register({
      id,
      title,
      group: '编辑',
      section: 1,
      order,
      submenu: SUBMENU,
      enabled: (s) => s.hasDocument,
      run: async (services) => {
        if (!(await action())) services.hint(NO_LAYER_HINT);
      },
    });
  };

  registerQuick('edit.transform.rotate180', '旋转 180°', 6, () => deps.rotate180());
  registerQuick('edit.transform.rotate90cw', '顺时针旋转 90°', 7, () => deps.rotate90('cw'));
  registerQuick('edit.transform.rotate90ccw', '逆时针旋转 90°', 8, () => deps.rotate90('ccw'));
  registerQuick('edit.transform.flipHorizontal', '水平翻转', 9, () => deps.flip('horizontal'));
  registerQuick('edit.transform.flipVertical', '垂直翻转', 10, () => deps.flip('vertical'));
}
